import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";

const RADIUS = 22;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const splitSentences = (text) => {
  if (!text) return [];
  const result = [];
  for (const line of text.split(/\n/)) {
    for (const part of line.split(/(?<=[.!?])(?=[ \t])/)) {
      const trimmed = part.trim();
      if (trimmed) result.push(trimmed);
    }
  }
  return result;
};

// Multi-script presentation. Walks through each script's sentences with a
// timer, then stops between scripts and offers Restart-this-section / Continue.
const MegaPresentation = forwardRef(
  (
    {
      scripts = [],
      fontSize = 48,
      sentenceDuration = 5,
      mirrorHorizontal = false,
      countdownInit = 5,
      startMode = "countdown",
      showNextSentence = false,
      onSectionDone,
    },
    ref
  ) => {
    const [scriptIndex, setScriptIndex] = useState(0);
    const [active, setActive] = useState(false);
    const [sentIndex, setSentIndex] = useState(0);
    const [timer, setTimer] = useState(sentenceDuration);
    const [isCountingDown, setIsCountingDown] = useState(false);
    const [countdown, setCountdown] = useState(null);
    const [awaitingStart, setAwaitingStart] = useState(false);
    const [showBetweenScreen, setShowBetweenScreen] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const containerRef = useRef(null);
    const indexRef = useRef(0);
    const timerRef = useRef(sentenceDuration);
    const durationRef = useRef(sentenceDuration);
    const countdownRef = useRef(null);

    durationRef.current = sentenceDuration;

    const currentScript = scripts[scriptIndex] ?? null;
    const nextScript = scripts[scriptIndex + 1] ?? null;
    const isLast = scriptIndex >= scripts.length - 1;

    const sentences = useMemo(() => splitSentences(currentScript?.text ?? ""), [currentScript?.text]);

    useEffect(() => {
      const handler = () => setIsFullscreen(!!document.fullscreenElement);
      document.addEventListener("fullscreenchange", handler);
      return () => document.removeEventListener("fullscreenchange", handler);
    }, []);

    const goFullscreen = async () => {
      const el = containerRef.current;
      if (!el || document.fullscreenElement) return;
      const req =
        el.requestFullscreen ||
        el.webkitRequestFullscreen ||
        el.mozRequestFullScreen ||
        el.msRequestFullscreen;
      try {
        if (req) await req.call(el);
      } catch (_) {}
    };

    const exitFullscreen = () => {
      const fn =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.mozCancelFullScreen ||
        document.msExitFullscreen;
      if (fn) fn.call(document);
    };

    const resetSlides = () => {
      indexRef.current = 0;
      timerRef.current = durationRef.current;
      setSentIndex(0);
      setTimer(durationRef.current);
    };

    const beginCountdown = () => {
      countdownRef.current = countdownInit;
      setCountdown(countdownInit);
      setIsCountingDown(true);
    };

    // Reset per-section state, then either gate behind a start button or
    // begin countdown / playback based on mode.
    const playSection = (useStartButtonGate) => {
      setActive(false);
      setIsCountingDown(false);
      setCountdown(null);
      setShowBetweenScreen(false);
      resetSlides();

      if (useStartButtonGate && startMode !== "countdown") {
        if (startMode === "countdownAndStartbutton") {
          beginCountdown();
        } else {
          setActive(true);
        }
        return;
      }

      if (startMode === "countdown") {
        beginCountdown();
      } else {
        setAwaitingStart(true);
      }
    };

    const start = async () => {
      await goFullscreen();
      setScriptIndex(0);
      requestAnimationFrame(() => playSection(false));
    };

    const handleInDisplayStart = () => {
      setAwaitingStart(false);
      if (startMode === "countdownAndStartbutton") {
        beginCountdown();
      } else {
        setActive(true);
      }
    };

    const handleContinue = () => {
      if (isLast) return;
      onSectionDone && onSectionDone(scriptIndex);
      setScriptIndex((i) => i + 1);
      requestAnimationFrame(() => playSection(true));
    };

    const handleRestartSection = () => playSection(true);

    const handleFinish = () => {
      onSectionDone && onSectionDone(scriptIndex);
      setShowBetweenScreen(false);
      exitFullscreen();
    };

    const pause = () => setActive(false);
    const play = () => setActive(true);
    const toggle = () => setActive((a) => !a);

    const restart = () => {
      setScriptIndex(0);
      setActive(false);
      setIsCountingDown(false);
      setCountdown(null);
      setAwaitingStart(false);
      setShowBetweenScreen(false);
      resetSlides();
    };

    useImperativeHandle(
      ref,
      () => ({ start, pause, play, toggle, restart, getState: () => ({ scriptIndex, active }) }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [scriptIndex, active, startMode, countdownInit, scripts.length]
    );

    // Countdown -> start slideshow
    useEffect(() => {
      if (!isCountingDown) return;
      const id = setInterval(() => {
        countdownRef.current -= 1;
        if (countdownRef.current <= 0) {
          clearInterval(id);
          setIsCountingDown(false);
          setCountdown(null);
          setActive(true);
        } else {
          setCountdown(countdownRef.current);
        }
      }, 1000);
      return () => clearInterval(id);
    }, [isCountingDown]);

    // Slideshow tick — advance sentence at end of duration, end of script triggers between-screen
    useEffect(() => {
      if (!active) return;
      const id = setInterval(() => {
        timerRef.current -= 1;
        if (timerRef.current <= 0) {
          const nextIdx = indexRef.current + 1;
          if (nextIdx >= sentences.length) {
            setActive(false);
            setShowBetweenScreen(true);
            return;
          }
          indexRef.current = nextIdx;
          timerRef.current = durationRef.current;
          setSentIndex(nextIdx);
          setTimer(durationRef.current);
        } else {
          setTimer(timerRef.current);
        }
      }, 1000);
      return () => clearInterval(id);
    }, [active, sentences.length]);

    const progress = timer / sentenceDuration;
    const dashOffset = CIRCUMFERENCE * (1 - progress);
    const sentence = sentences[sentIndex] ?? "";
    const nextSentence = sentences[sentIndex + 1] ?? "";

    return (
      <div
        ref={containerRef}
        style={{
          position: "relative",
          width: "100%",
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#0d0d0d",
          borderRadius: "8px",
          padding: "40px",
        }}
      >
        {isFullscreen && (
          <button
            onClick={exitFullscreen}
            title="Exit fullscreen"
            style={{
              position: "absolute",
              top: 26,
              right: 90,
              zIndex: 20,
              width: 40,
              height: 40,
              borderRadius: "50%",
              border: "none",
              cursor: "pointer",
              background: "rgba(0,0,0,0.5)",
              color: "#fff",
              fontSize: 22,
              fontWeight: 700,
              lineHeight: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        )}

        {/* Timer ring — upper right */}
        <div style={{ position: "absolute", top: 16, right: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="60" height="60">
            <circle cx="30" cy="30" r={RADIUS} fill="none" stroke="#333" strokeWidth="4" />
            <circle
              cx="30"
              cy="30"
              r={RADIUS}
              fill="none"
              stroke="#f5c518"
              strokeWidth="4"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
              transform="rotate(-90 30 30)"
              style={{ transition: "stroke-dashoffset 0.9s linear" }}
            />
            <text x="30" y="35" textAnchor="middle" fill="#f5c518" fontSize="16" fontWeight="700">
              {timer}
            </text>
          </svg>
        </div>

        {/* Section + sentence counter — upper left */}
        {scripts.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: 16,
              left: 16,
              zIndex: 15,
              background: "rgba(0,0,0,0.45)",
              color: "#fff",
              padding: "4px 10px",
              borderRadius: 12,
              fontSize: 13,
              fontWeight: 600,
              fontFamily: "Montserrat, sans-serif",
            }}
          >
            {scriptIndex + 1} / {scripts.length}
            {currentScript?.title ? ` — ${currentScript.title}` : ""}
            {sentences.length > 0 && (
              <span style={{ opacity: 0.65, marginLeft: 8 }}>
                ({sentIndex + 1}/{sentences.length})
              </span>
            )}
          </div>
        )}

        {/* Current sentence, plus an optional dimmed preview of the next one so
            the speaker is not left hanging at the end of a sentence. */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: `${Math.round(fontSize * 0.5)}px`,
            maxWidth: "80%",
            transform: mirrorHorizontal ? "scaleX(-1)" : undefined,
          }}
        >
          <p
            style={{
              fontSize: `${fontSize}px`,
              color: "#fff",
              textAlign: "center",
              lineHeight: 1.4,
              margin: 0,
            }}
          >
            {sentence}
          </p>
          {showNextSentence && nextSentence && (
            <p
              style={{
                fontSize: `${Math.round(fontSize * 0.55)}px`,
                color: "rgba(255,255,255,0.38)",
                textAlign: "center",
                lineHeight: 1.4,
                margin: 0,
              }}
            >
              {nextSentence}
            </p>
          )}
        </div>

        {/* Countdown overlay */}
        {isCountingDown && (
          <div style={overlayStyle}>
            <div style={{ fontSize: "6rem", color: "#fff", fontWeight: 700, lineHeight: 1 }}>
              {countdown}
            </div>
          </div>
        )}

        {/* In-display start button */}
        {awaitingStart && (
          <div style={{ ...overlayStyle, zIndex: 11 }}>
            <button onClick={handleInDisplayStart} style={bigButtonStyle("#038edc")}>
              Start
            </button>
          </div>
        )}

        {/* Between-script overlay */}
        {showBetweenScreen && (
          <div style={{ ...overlayStyle, zIndex: 12, flexDirection: "column", gap: 18 }}>
            <div style={{ color: "#fff", fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
              {isLast
                ? "All sections done."
                : `Section finished. Next: ${nextScript?.title || `Section ${scriptIndex + 2}`}`}
            </div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", justifyContent: "center" }}>
              <button onClick={handleRestartSection} style={bigButtonStyle("#6b7280")}>
                ↻ Restart this section
              </button>
              {isLast ? (
                <button onClick={handleFinish} style={bigButtonStyle("#038edc")}>
                  Finish and mark filmed
                </button>
              ) : (
                <button onClick={handleContinue} style={bigButtonStyle("#038edc")}>
                  Next and mark filmed
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

const overlayStyle = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(0,0,0,0.65)",
  zIndex: 10,
};

const bigButtonStyle = (bg) => ({
  padding: "16px 32px",
  borderRadius: 12,
  border: "none",
  cursor: "pointer",
  background: bg,
  color: "#fff",
  fontFamily: "Montserrat, sans-serif",
  fontWeight: 700,
  fontSize: 18,
  boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
});

MegaPresentation.displayName = "MegaPresentation";
export default MegaPresentation;
